import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import { match } from 'ts-pattern';

import APIGateway, { type ActionLogResponse, type ModelLogResponse, type RequestLogResponse } from '@/api';
import { AgentTraceViewer, type TraceUrlState } from '@/app/agents/details/traceViewer/traceViewer';
import { Button } from '@/components/button/button';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Icon } from '@/components/icon/icon';
import { Input } from '@/components/input/input';
import { Loader } from '@/components/loader/loader';
import ActionLogViewer from '@/components/logs/LogViewers/actionLogViewer';
import ModelLogViewer from '@/components/logs/LogViewers/modelLogViewer';
import RequestResponseLogViewer from '@/components/logs/LogViewers/requestResponseLogViewer';
import { Heading } from '@/components/typography/heading';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';

import styles from './page.module.scss';


type LogType = 'modelLog' | 'actionLog' | 'requestResponseLog' | 'trace';

type LogData =
  | { type: 'none'; data: Record<string, never> }
  | { type: 'actionLog'; data: ActionLogResponse }
  | { type: 'modelLog'; data: ModelLogResponse }
  | { type: 'requestResponseLog'; data: RequestLogResponse };

const LOG_TYPE_LABELS: Record<LogType, string> = {
  modelLog: 'Model Log',
  actionLog: 'Action Log',
  requestResponseLog: 'Request/Response',
  trace: 'Traces'
};

function isLogType( value: string | null | undefined ): value is LogType {
  return !!value && Object.hasOwn( LOG_TYPE_LABELS, value );
}

/** Build a URLSearchParams from explicit values, omitting empty/default ones. */
function buildParams( values: Record<string, string | number | undefined> ): URLSearchParams {
  const params = new URLSearchParams();
  for ( const [ key, value ] of Object.entries( values ) ) {
    if ( value !== undefined && value !== '' ) {
      params.set( key, String( value ) );
    }
  }
  return params;
}

export default function SettingsPage() {
  const [ searchParams ] = useSearchParams();

  // Read all URL params once on mount via useState initializers
  // URL param names match API payload: agent_id, request_id, correlation_id
  const [ useLocalZone ] = useLocalZonePreference();
  const [ agentId, setAgentId ] = useState<string>( () => searchParams.get( 'agentId' ) ?? '' );
  const [ correlationId, setCorrelationId ] = useState<string>( () => searchParams.get( 'correlationId' ) ?? '' );
  const [ currentPage, setCurrentPage ] = useState<number>( () => parseInt( searchParams.get( 'page' ) ?? '1', 10 ) || 1 );
  const [ logType, setLogType ] = useState<LogType>( () => {
    const p = searchParams.get( 'logType' );
    return isLogType( p ) ? p : 'modelLog';
  } );
  const [ isLoading, setIsLoading ] = useState<boolean>( false );
  const [ logData, setLogData ] = useState<LogData>( { type: 'none', data: {} } );
  const [ requestId, setRequestId ] = useState<string>( () => searchParams.get( 'requestId' ) ?? '' );
  const [ sessionId, setSessionId ] = useState<string>( '' );
  const [ hasMorePages, setHasMorePages ] = useState<boolean>( false );
  const [ selectedId, setSelectedId ] = useState<string>( () => searchParams.get( 'selectedId' ) ?? '' );

  // Read initial trace URL state from search params (once on mount)
  const [ initialTraceState ] = useState<TraceUrlState>( () => ( {
    traceRequestId: searchParams.get( 'traceRequestId' ) ?? '',
    traceCorrelationId: searchParams.get( 'traceCorrelationId' ) ?? '',
    traceClientId: searchParams.get( 'traceClientId' ) ?? '',
    traceDateFrom: searchParams.get( 'traceDateFrom' ) ?? '',
    traceDateTo: searchParams.get( 'traceDateTo' ) ?? '',
    traceKeyword: searchParams.get( 'traceKeyword' ) ?? '',
    traceOffset: parseInt( searchParams.get( 'traceOffset' ) ?? '0', 10 ) || 0,
    selectedRequest: searchParams.get( 'selectedRequest' ) ?? ''
  } ) );

  // ── Ref-based URL sync ────────────────────────────────────────────────────
  const stateRef = useRef( { agentId, requestId, correlationId, logType, currentPage, selectedId } );
  stateRef.current = { agentId, requestId, correlationId, logType, currentPage, selectedId };

  /** Write URL from the latest state ref + optional overrides. */
  const writeUrl = ( overrides: Partial<typeof stateRef.current> = {} ) => {
    const s = { ...stateRef.current, ...overrides };
    const params = s.logType === 'trace'
      ? buildParams( { logType: 'trace', agentId: s.agentId || undefined } )
      : buildParams( {
          logType: s.logType,
          agentId: s.agentId || undefined,
          requestId: s.requestId || undefined,
          correlationId: s.correlationId || undefined,
          page: s.currentPage > 1 ? s.currentPage : undefined,
          selectedId: s.selectedId || undefined
        } );
    const url = new URL( window.location.href );
    url.search = params.toString();
    window.history.replaceState( null, '', url.toString() );
  };

  // ── Auto-fetch on shared link ─────────────────────────────────────────────
  // State is already initialized from URL params via useState initializers above.
  // Auto-fetch when URL contains any meaningful state (filters or a selected item).
  useEffect( () => {
    if ( logType === 'trace' ) return;
    if ( !agentId && !requestId && !correlationId && !selectedId ) return;
    fetchLogData( currentPage );
  }, [] );

  const handleAgentIdChange = ( e: React.ChangeEvent<HTMLInputElement> ) => {
    setAgentId( e.target.value );
    setSessionId( '' );
  };

  const handleRequestIdChange = ( e: React.ChangeEvent<HTMLInputElement> ) => {
    setRequestId( e.target.value );
    setSessionId( '' );
  };

  const handleCorrelationIdChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCorrelationId( e.target.value );
    setSessionId( '' );
  };

  const handleLogTypeChange = ( type: LogType ) => {
    setLogType( type );
    setSessionId( '' );
    setSelectedId( '' );
    setLogData( { type: 'none', data: {} } );
    writeUrl( { logType: type, selectedId: '' } );
  };

  const handleSearch = () => {
    setCurrentPage( 1 );
    setSessionId( '' );
    setSelectedId( '' );
    writeUrl( { currentPage: 1, selectedId: '' } );
    fetchLogData( 1 );
  };

  const handleIncrement = () => {
    if ( !hasMorePages ) return;
    const nextPage = currentPage + 1;
    setCurrentPage( nextPage );
    setSelectedId( '' );
    writeUrl( { currentPage: nextPage, selectedId: '' } );
    fetchLogData( nextPage );
  };

  const handleDecrement = () => {
    if ( currentPage === 1 ) return;
    const prevPage = currentPage - 1;
    setCurrentPage( prevPage );
    setSelectedId( '' );
    writeUrl( { currentPage: prevPage, selectedId: '' } );
    fetchLogData( prevPage );
  };

  const handleSelectionChange = ( id: string ) => {
    setSelectedId( id );
    writeUrl( { selectedId: id } );
  };

  const handleTraceUrlStateChange = ( state: TraceUrlState ) => {
    const params = buildParams( {
      logType: 'trace',
      agentId: stateRef.current.agentId || undefined,
      traceRequestId: state.traceRequestId || undefined,
      traceCorrelationId: state.traceCorrelationId || undefined,
      traceClientId: state.traceClientId || undefined,
      traceDateFrom: state.traceDateFrom || undefined,
      traceDateTo: state.traceDateTo || undefined,
      traceKeyword: state.traceKeyword || undefined,
      traceOffset: state.traceOffset > 0 ? state.traceOffset : undefined,
      selectedRequest: state.selectedRequest || undefined
    } );
    const url = new URL( window.location.href );
    url.search = params.toString();
    window.history.replaceState( null, '', url.toString() );
  };

  const fetchLogData = async( page: number ) => {
    try {
      setIsLoading( true );
      const offset_num = ( page - 1 ) * 10;

      await match( logType )
        .with( 'actionLog', async() => {
          const res = await APIGateway.logs.actionLogsGet(
            agentId, 10, offset_num, requestId, correlationId, sessionId
          );
          setLogData( { type: 'actionLog', data: res } );
          setSessionId( res.session_id );
          setHasMorePages( res.agent_actions.length === 10 );
        } )
        .with( 'modelLog', async() => {
          const res = await APIGateway.logs.modelLogsGet(
            agentId, 10, offset_num, requestId, correlationId, sessionId
          );
          setLogData( { type: 'modelLog', data: res } );
          setSessionId( res.session_id );
          setHasMorePages( res.model_invocations.length === 10 );
        } )
        .with( 'requestResponseLog', async() => {
          const res = await APIGateway.logs.requestLogsGet(
            agentId, 10, offset_num, requestId, correlationId, sessionId
          );
          setLogData( { type: 'requestResponseLog', data: res } );
          setSessionId( res.session_id );
          setHasMorePages( res.completion_requests.length === 10 );
        } )
        .with( 'trace', async() => {
          // Trace view is handled by the AgentTraceViewer component directly
        } )
        .exhaustive();
    } catch( e ) {
      console.error( e );
    } finally {
      setIsLoading( false );
    }
  };

  return (
    <div className="page">
      <div className={ clsx( 'layout__row', 'pad', 'layout__info' ) }>
        <span className='info_name'>
          <Heading as="h2">Log Viewer</Heading>
        </span>
      </div>
      <div className={ styles.toolbar }>
        <Input type="text" label="Agent ID" placeholder="Agent ID" value={ agentId } onChange={ handleAgentIdChange } />
        {logType !== 'trace' && (
          <>
            <Input type="text" label="Request id" placeholder="Request id" value={ requestId } onChange={ handleRequestIdChange } />
            <Input
              type="text"
              label="Correlation id"
              placeholder='Correlation id'
              value={ correlationId }
              onChange={ handleCorrelationIdChange }
            />
          </>
        )}
        <Menu
          label="Log type"
          value={ LOG_TYPE_LABELS[logType] }
          variant="select"
        >
          <MenuItem value="Model Log" onClick={ () => handleLogTypeChange( 'modelLog' ) } />
          <MenuItem value="Request/Response" onClick={ () => handleLogTypeChange( 'requestResponseLog' ) } />
          <MenuItem value="Action Log" onClick={ () => handleLogTypeChange( 'actionLog' ) } />
          <MenuItem value="Traces" onClick={ () => handleLogTypeChange( 'trace' ) } />
        </Menu>
        {logType !== 'trace' && (
          <>
            <div className={ styles.pagination }>
              <a
                onClick={ handleDecrement }
                className={ clsx( currentPage === 1 && styles.disabled ) }
                style={ {
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1
                } }
              >
                <Icon icon={ FiChevronLeft } size="lg" />
              </a>
              <span style={ { margin: '0 10px' } }>{currentPage}</span>
              <a
                onClick={ handleIncrement }
                className={ clsx( !hasMorePages && styles.disabled ) }
                style={ {
                  cursor: !hasMorePages ? 'not-allowed' : 'pointer',
                  opacity: !hasMorePages ? 0.5 : 1
                } }
              >
                <Icon icon={ FiChevronRight } size="lg" />
              </a>
            </div>
            <Button variant='secondary' onClick={ handleSearch }>
              Search
            </Button>
          </>
        )}
      </div>
      <div className="body">
        {logType === 'trace' ? (
          agentId ? (
            <AgentTraceViewer
              agentId={ agentId }
              useLocalZone={ useLocalZone }
              initialUrlState={ initialTraceState }
              onUrlStateChange={ handleTraceUrlStateChange }
            />
          ) : (
            <div className={ styles.loader }>
              <p>Enter an agent name to view traces.</p>
            </div>
          )
        ) : isLoading ? (
          <div className={ styles.loader }>
            <Loader />
          </div>
        ) : (
          <>
            {logData.type === 'actionLog' && (
              <ActionLogViewer
                agentId={ agentId }
                requestId={ requestId }
                correlactionId={ correlationId }
                data={ logData.data.agent_actions }
                initialSelectedId={ selectedId }
                onSelectionChange={ handleSelectionChange }
              />
            )}
            {logData.type === 'modelLog' && (
              <ModelLogViewer
                agentId={ agentId }
                requestId={ requestId }
                correlactionId={ correlationId }
                data={ logData.data.model_invocations }
                initialSelectedId={ selectedId }
                onSelectionChange={ handleSelectionChange }
              />
            )}
            {logData.type === 'requestResponseLog' && (
              <RequestResponseLogViewer
                agentId={ agentId }
                requestId={ requestId }
                correlationId={ correlationId }
                data={ logData.data.completion_requests }
                initialSelectedId={ selectedId }
                onSelectionChange={ handleSelectionChange }
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
